import { useState } from 'react';
import { Dialog, DialogContent } from '../ui/dialog';
import { X, Maximize2 } from 'lucide-react';

/**
 * Click an image (or its wrapped trigger) to open a centered full-size
 * lightbox dialog. iter270 — added so tournament banners, gallery
 * thumbnails, and any other clickable preview can pop a larger view
 * without a separate gallery page.
 *
 * Props:
 *   src                — image URL (required when `children` is omitted)
 *   alt                — accessible label (defaults to '')
 *   className          — extra classes on the trigger image
 *   triggerClassName   — wrapper class around the trigger
 *   children           — optional custom trigger; if provided the default
 *                        <img> is replaced (useful for icon thumbnails)
 *   testId             — applied to the trigger for testing
 */
export default function ImageLightbox({
  src,
  alt = '',
  className = '',
  triggerClassName = '',
  children,
  testId,
}) {
  const [open, setOpen] = useState(false);
  if (!src) return null;

  return (
    <>
      <button
        type="button"
        className={`relative group cursor-zoom-in p-0 border-0 bg-transparent ${triggerClassName}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        data-testid={testId || 'image-lightbox-trigger'}
        aria-label="View larger image"
      >
        {children || (
          <img
            src={src}
            alt={alt}
            className={`object-cover rounded-lg border shadow-sm transition group-hover:opacity-90 ${className}`}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        <span className="absolute top-2 right-2 bg-white/85 rounded-full p-1 shadow opacity-0 group-hover:opacity-100 transition">
          <Maximize2 className="h-3.5 w-3.5 text-slate-700" />
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-5xl p-0 bg-transparent border-0 shadow-none"
          data-testid="image-lightbox-dialog"
        >
          <div className="relative">
            <img
              src={src}
              alt={alt}
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5 transition"
              data-testid="image-lightbox-close"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
